import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, StatCard, Panel, StatusBadge, EmptyState, money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { TimeZones, AnalyticsMini } from "@/components/DashboardWidgets";
import {
  Users, Truck, CurrencyDollar, TrendUp, ClipboardText, CheckCircle, Clock,
  Package, SteeringWheel, Receipt, ChartLineUp, ShieldWarning, UserPlus, SignIn, SignOut, Target,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
  PieChart, Pie,
} from "recharts";

const colors = ["#2563eb", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6"];

function TT({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="text-slate-700 font-semibold capitalize">{label}</p>
      {payload.map((p, i) => <p key={i} className="text-slate-500">{p.name}: <span className="text-slate-900 font-semibold tabular">{p.value}</span></p>)}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data: stats, isLoading } = useList("dashboard", "/dashboard", { refetchInterval: 15000 });
  const { data: loads } = useList("loads", "/loads", { enabled: user.role !== "sales" });

  if (isLoading || !stats) return <div className="text-slate-400">Loading dashboard…</div>;
  const greeting = user.name?.split(" ")[0] || "there";

  return (
    <div>
      <PageHeader
        title={`Good day, ${greeting}`}
        subtitle={new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
      />
      <div className="mb-6">
        <TimeZones />
      </div>
      {user.role === "admin" && <AdminDash stats={stats} loads={loads} />}
      {user.role === "sales" && <SalesDash stats={stats} />}
      {user.role === "dispatcher" && <DispatcherDash stats={stats} loads={loads} />}
    </div>
  );
}

function AdminDash({ stats, loads }) {
  const { data: expiring = [] } = useList("expiring", "/compliance/expiring");
  const { data: acct } = useList("accounting-summary", "/accounting/summary");
  const pipeData = Object.entries(stats.pipeline || {}).map(([status, count]) => ({ status, count }));
  const loadData = [
    { status: "pending", count: (loads || []).filter(l => l.status === "pending").length },
    { status: "active", count: stats.active_loads },
    { status: "completed", count: stats.completed_loads },
  ];
  const recentLoads = (loads || []).slice(0, 6);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Receivable" value={money(acct?.receivable)} sub="outstanding invoices" icon={CurrencyDollar} accent="blue" delay={0} />
        <StatCard label="Active Loads" value={stats.active_loads} sub={`${stats.total_loads} total`} icon={Truck} accent="blue" delay={50} />
        <StatCard label="Fleet" value={stats.active_trucks || 0} sub={`${stats.trucks || 0} ${(stats.trucks === 1) ? "truck" : "trucks"} · ${stats.trailers || 0} ${(stats.trailers === 1) ? "trailer" : "trailers"}`} icon={Package} accent="violet" delay={100} />
        <StatCard label="Drivers" value={stats.active_drivers || 0} sub={`${stats.drivers || 0} total`} icon={SteeringWheel} accent="sky" delay={150} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Leads" value={stats.total_leads} sub={`${stats.conversion_rate}% conversion`} icon={Users} accent="blue" delay={0} />
        <StatCard label="Open Tasks" value={stats.open_tasks} icon={ClipboardText} accent="amber" delay={50} />
        <StatCard label="Collected" value={money(acct?.collected)} sub="paid invoices" icon={TrendUp} accent="green" delay={100} />
        <StatCard label="Expiring Docs" value={stats.expiring_docs || 0} sub="next 30 days" trend={stats.expiring_docs ? "down" : null} icon={ShieldWarning} accent="amber" delay={150} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel className="lg:col-span-2 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Load Volume by Status</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={loadData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
              <XAxis dataKey="status" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip content={<TT />} cursor={{ fill: "#f1f5f9" }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>{loadData.map((e, i) => <Cell key={i} fill={colors[i % colors.length]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel className="p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Lead Pipeline</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pipeData} dataKey="count" nameKey="status" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3}>
                {pipeData.map((e, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
              </Pie>
              <Tooltip content={<TT />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 justify-center mt-2">
            {pipeData.map((d, i) => <span key={d.status} className="flex items-center gap-1.5 text-xs text-slate-500 capitalize"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: colors[i] }} />{d.status}</span>)}
          </div>
        </Panel>
      </div>

      <Panel>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2"><Truck size={18} className="text-blue-600" /><h3 className="font-heading font-bold text-slate-900">Recent Loads</h3></div>
        {recentLoads.length === 0 ? <EmptyState icon={Truck} title="No loads yet" hint="Create loads to see them here." /> : (
          <div className="divide-y divide-slate-100">
            {recentLoads.map((l) => (
              <div key={l.id} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                <div><p className="font-mono text-sm text-slate-800">{l.reference}</p><p className="text-xs text-slate-400">{l.origin} → {l.destination}</p></div>
                <div className="flex items-center gap-4"><span className="font-mono text-sm text-slate-700">{money(l.rate)}</span><StatusBadge status={l.status} /></div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2"><ShieldWarning size={18} className="text-amber-600" /><h3 className="font-heading font-bold text-slate-900">Expiring Compliance</h3><span className="text-xs text-slate-400">next 30 days</span></div>
        {expiring.length === 0 ? <EmptyState icon={ShieldWarning} title="All documents current" hint="No CDLs, medical cards or registrations expiring soon." /> : (
          <div className="divide-y divide-slate-100" data-testid="expiring-compliance">
            {expiring.slice(0, 8).map((e, i) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                <div>
                  <p className="text-sm font-medium text-slate-800">{e.entity_name || "—"} <span className="text-xs text-slate-400 capitalize">· {e.entity}</span></p>
                  <p className="text-xs text-slate-400 capitalize">{e.type}{e.number ? ` · ${e.number}` : ""}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm text-slate-600">{e.expiry_date}</p>
                  <span className={`text-xs font-semibold ${e.days_left < 0 ? "text-red-600" : "text-amber-600"}`}>{e.days_left < 0 ? "expired" : `${e.days_left}d left`}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function SalesDash({ stats }) {
  const { data: act } = useList("my-activity", "/sales/my-activity", { refetchInterval: 30000 });
  const invalidate = useInvalidate();
  const today = act?.attendance?.today;
  const t = act?.totals || {};
  const funnel = [
    { status: "new", count: stats.new_leads }, { status: "contacted", count: stats.contacted },
    { status: "converted", count: stats.converted }, { status: "lost", count: stats.lost },
  ];
  const mark = async (kind) => {
    try { await api.post(`/attendance/${kind}`); window.dispatchEvent(new Event("shift-refresh")); invalidate(["my-activity"]); toast.success(kind === "check-in" ? "Checked in" : "Checked out"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3">
        <Link to="/submit-lead" data-testid="dash-submit-lead" className="group flex-1 flex items-center gap-3 rounded-xl bg-blue-600 text-white px-5 py-4 shadow-sm hover:bg-blue-700 transition-colors">
          <div className="w-10 h-10 rounded-lg bg-white/15 grid place-items-center"><UserPlus size={20} weight="bold" /></div>
          <div><p className="font-heading font-bold">Submit a new lead</p><p className="text-xs text-blue-100">Onboard a carrier prospect</p></div>
        </Link>
        <Panel className="flex items-center gap-3 px-5 py-4 sm:w-auto">
          <div className="flex-1">
            <p className="text-xs text-slate-400">Today</p>
            <p className="font-heading font-bold text-slate-900 text-sm">{today?.check_in ? (today?.check_out ? "Shift complete" : "Checked in") : "Not checked in"}</p>
          </div>
          {!today?.check_in ? (
            <Button onClick={() => mark("check-in")} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="dash-check-in"><SignIn size={16} className="mr-1.5" />Check In</Button>
          ) : !today?.check_out ? (
            <Button onClick={() => mark("check-out")} variant="outline" className="border-slate-300 bg-white font-semibold" data-testid="dash-check-out"><SignOut size={16} className="mr-1.5" />Check Out</Button>
          ) : <CheckCircle size={24} weight="fill" className="text-emerald-500" />}
        </Panel>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Open Leads" value={t.open || 0} icon={CurrencyDollar} accent="blue" delay={0} />
        <StatCard label="Converted (mo)" value={t.converted || 0} sub="deals won" icon={CheckCircle} accent="green" trend="up" delay={50} />
        <StatCard label="Commission (mo)" value={money(act?.commission_month || 0)} sub={`${act?.commission_rate || 0}% rate`} icon={TrendUp} accent="violet" delay={100} />
        <StatCard label="Win Rate / Score" value={`${act?.win_rate || 0}%`} sub="conversion score" icon={ChartLineUp} accent="amber" delay={150} />
      </div>

      {act?.monthly_target > 0 && (
        <Panel className="p-5" data-testid="sales-target">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><Target size={18} className="text-blue-600" />Monthly Progress</h3>
            <span className="text-sm text-slate-500">{act.progress}%</span>
          </div>
          <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${Math.min(100, act.progress)}%` }} />
          </div>
          <p className="text-xs text-slate-400 mt-1.5">{act.progress}% of your monthly goal achieved</p>
        </Panel>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel className="p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-4">My Pipeline</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={funnel}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
              <XAxis dataKey="status" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip content={<TT />} cursor={{ fill: "#f1f5f9" }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>{funnel.map((e, i) => <Cell key={i} fill={colors[i % colors.length]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-slate-900">Lead Breakdown</h3>
            <Link to="/leads" className="text-sm font-semibold text-blue-600 hover:text-blue-700">Manage leads →</Link>
          </div>
          <div className="space-y-3">
            {[
              { label: "Open / In-progress", v: t.open, c: "text-blue-600" },
              { label: "Converted", v: t.converted, c: "text-emerald-600" },
              { label: "Lost", v: t.lost, c: "text-slate-500" },
              { label: "Rejected", v: t.rejected, c: "text-red-500" },
            ].map((r) => (
              <div key={r.label} className="flex items-center justify-between border-b border-slate-100 pb-2">
                <span className="text-sm text-slate-600">{r.label}</span>
                <span className={`font-heading font-bold ${r.c}`}>{r.v || 0}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function DispatcherDash({ stats, loads }) {
  const data = [
    { status: "pending", count: stats.pending_loads }, { status: "active", count: stats.active_loads },
    { status: "completed", count: stats.completed_loads },
  ];
  const myLoads = (loads || []).slice(0, 6);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="My Loads" value={stats.my_loads} icon={Truck} accent="blue" delay={0} />
        <StatCard label="Active" value={stats.active_loads} icon={Package} accent="sky" delay={50} />
        <StatCard label="Completed" value={stats.completed_loads} icon={CheckCircle} accent="green" delay={100} />
        <StatCard label="Unpaid Invoices" value={stats.unpaid_invoices} icon={Receipt} accent="red" delay={150} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel className="p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Load Status</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data} dataKey="count" nameKey="status" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3}>
                {data.map((e, i) => <Cell key={i} fill={colors[(i + 1) % colors.length]} />)}
              </Pie>
              <Tooltip content={<TT />} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>
        <Panel className="lg:col-span-2">
          <div className="px-5 py-4 border-b border-slate-200"><h3 className="font-heading font-bold text-slate-900">My Active Loads</h3></div>
          {myLoads.length === 0 ? <EmptyState icon={Truck} title="No loads assigned" /> : (
            <div className="divide-y divide-slate-100">
              {myLoads.map((l) => (
                <div key={l.id} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50">
                  <div><p className="font-mono text-sm text-slate-800">{l.reference}</p><p className="text-xs text-slate-400">{l.origin} → {l.destination}</p></div>
                  <StatusBadge status={l.status} />
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
